// @ts-check
// Configurações > Automações (contrato 08, seção 7): lista com ativar e desativar, resumo do gatilho, última
// execução e o histórico de execuções de todas as automações.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { listAutomations, updateAutomation } from '../../data/automations.js';
import { Link, navigate } from '../../app/router.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { EmptyState, Skeleton, Toggle } from '../../ui/feedback.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatRelativeToNow } from '../../lib/format.js';
import { AutomationRunList } from './automation-runs.js';
import { invalidateManualAutomations } from './manual-automations.js';
import { RUN_STATUS_COLORS, TRIGGER_ICONS, describeTrigger } from './automation-labels.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/automations.js').Automation} Automation */

/**
 * @param {{ automation: Automation, isAdmin: boolean, onToggled: (automation: Automation) => void }} props
 */
function AutomationRow({ automation, isAdmin, onToggled }) {
  const [isSaving, setIsSaving] = useState(false);
  const toggle = async (/** @type {boolean} */ isEnabled) => {
    if (!isAdmin || isSaving) return;
    setIsSaving(true);
    try {
      const updated = await updateAutomation(automation.id, { isEnabled });
      invalidateManualAutomations();
      onToggled(updated);
      showSuccessSnackbar(isEnabled ? t('automations.enabledSnackbar', { name: automation.name }) : t('automations.disabledSnackbar', { name: automation.name }));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };
  const lastRun = automation.lastRun;
  return html`<div class="automation-row" data-enabled=${automation.isEnabled ? 'true' : 'false'}>
    <div class="automation-row-toggle" title=${isAdmin ? undefined : t('automations.adminOnly')}>
      ${isAdmin
        ? html`<${Toggle} value=${automation.isEnabled} ariaLabel=${t('automations.toggleLabel', { name: automation.name })} onChange=${toggle} />`
        : html`<span class="automation-row-state">${automation.isEnabled ? t('automations.on') : t('automations.off')}</span>`}
    </div>
    <${Link} href=${`/settings/automations/${automation.id}`} className="automation-row-main">
      <span class="automation-row-icon"><${Icon} name=${TRIGGER_ICONS[automation.triggerType] ?? 'IconSettingsAutomation'} size=${16} /></span>
      <span class="automation-row-texts">
        <span class="automation-row-name">
          ${automation.name}
          ${automation.isPackage ? html`<span class="automation-row-badge">${t('automations.ready')}</span>` : null}
        </span>
        <span class="automation-row-trigger">${describeTrigger(automation)}</span>
      </span>
      <span class="automation-row-status">
        ${lastRun
          ? html`<${Tag} color=${RUN_STATUS_COLORS[/** @type {keyof typeof RUN_STATUS_COLORS} */ (lastRun.status)] ?? 'gray'} text=${t(`automations.runStatus.${lastRun.status}`)} />
              <span class="automation-row-time">${formatRelativeToNow(lastRun.createdAt)}</span>`
          : html`<span class="automation-row-time">${t('automations.neverRan')}</span>`}
        ${automation.recentFailureCount > 0
          ? html`<span class="automation-row-failures" title=${t('automations.recentFailuresHint')}><${Icon} name="IconAlertTriangle" size=${12} />${automation.recentFailureCount}</span>`
          : null}
      </span>
      <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${16} /></span>
    <//>
  </div>`;
}

export function SettingsAutomationsPage() {
  const [automations, setAutomations] = useState(/** @type {Automation[] | null} */ (null));
  // a flag WORKFLOWS do papel decide quem altera automações (a API responde 403 aos demais)
  const isAdmin = hasCurrentPermissionFlag('WORKFLOWS');

  useEffect(() => {
    document.title = `${t('automations.title')} - ${t('navigation.settings')}`;
    listAutomations().then(setAutomations).catch((error) => {
      showErrorSnackbar(error);
      setAutomations([]);
    });
  }, []);

  const replace = (/** @type {Automation} */ updated) => setAutomations((current) => current?.map((item) => (item.id === updated.id ? updated : item)) ?? null);
  const packageAutomations = automations?.filter((automation) => automation.isPackage) ?? [];
  const customAutomations = automations?.filter((automation) => !automation.isPackage) ?? [];
  const actions = isAdmin
    ? html`<${Button} title=${t('automations.new')} icon="IconPlus" accent="blue" size="small" onClick=${() => navigate('/settings/automations/new')} />`
    : null;

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconSettingsAutomation" title=${t('automations.title')} actions=${actions} />`}>
    <div class="settings-page automation-settings-page">
      ${isAdmin
        ? html`<p class="marketing-flows-hint" data-testid="automation-flows-hint">
            <${Icon} name="IconSitemap" size=${16} />
            <span>${t('automations.flowsHint')}</span>
            <${Link} href="/campaigns/flows" className="operations-link">${t('automations.flowsHintLink')}<//>
          </p>`
        : null}
      <${SettingsSection} title=${t('automations.listTitle')} description=${t('automations.listDescription')}>
        ${automations === null
          ? html`<${Skeleton} height=${56} radius=${8} /><${Skeleton} height=${56} radius=${8} />`
          : html`
              ${customAutomations.length > 0
                ? html`<div class="automation-list">${customAutomations.map((automation) => html`<${AutomationRow} key=${automation.id} automation=${automation} isAdmin=${isAdmin} onToggled=${replace} />`)}</div>`
                : html`<${EmptyState} icon="IconSettingsAutomation" title=${t('automations.emptyTitle')} subtitle=${t('automations.emptySubtitle')}
                    action=${isAdmin ? html`<${Button} title=${t('automations.new')} icon="IconPlus" variant="secondary" size="small" onClick=${() => navigate('/settings/automations/new')} />` : null} />`}
            `}
      <//>
      ${packageAutomations.length > 0
        ? html`<${SettingsSection} title=${t('automations.packageTitle')} description=${t('automations.packageDescription')}>
            <div class="automation-list">${packageAutomations.map((automation) => html`<${AutomationRow} key=${automation.id} automation=${automation} isAdmin=${isAdmin} onToggled=${replace} />`)}</div>
          <//>`
        : null}
      <${SettingsSection} title=${t('automations.runs.title')} description=${t('automations.runs.description')}>
        <${AutomationRunList} isAdmin=${isAdmin} />
      <//>
    </div>
  <//>`;
}
